import { Links, Meta, Scripts, ScrollRestoration } from "@remix-run/react";

import { useContext, useEffect } from "react";
import { withEmotionCache } from "@emotion/react";
import ClientStyleContext from "./clientStyleContext.js";
import ServerStyleContext from "./serverStyleContext.js";
import { getOrganizationSchema } from "#app/utils/structuredData.js";

export default withEmotionCache(
  ({ env, user, colorMode, children }, emotionCache) => {
    const serverStyleData = useContext(ServerStyleContext);
    const clientStyleData = useContext(ClientStyleContext);

    // Only executed on client
    useEffect(() => {
      // re-link sheet container
      emotionCache.sheet.container = document.head;
      // re-inject tags
      const tags = emotionCache.sheet.tags;
      emotionCache.sheet.flush();
      tags.forEach((tag) => {
        emotionCache.sheet._insertTag(tag);
      });
      // reset cache to reapply global styles
      clientStyleData?.reset();
    }, []);

    return (
      <html
        lang="en"
        {...(colorMode && {
          "data-theme": colorMode,
          style: { colorScheme: colorMode },
        })}
      >
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <meta name="apple-mobile-web-app-title" content="Schnerp" />
          <meta name="theme-color" content="#625afa" />
          <link
            rel="preload"
            href="/fonts/Seravek-Regular.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href="/fonts/Seravek-Medium.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href="/fonts/Seravek-Bold.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="icon"
            type="image/png"
            href="/favicon-48x48-v2.png"
            sizes="48x48"
          />
          <link rel="icon" type="image/svg+xml" href="/favicon-v2.svg" />
          <link rel="shortcut icon" href="/favicon-v2.ico" />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href="/apple-touch-icon-v2.png"
          />
          <link rel="manifest" href="/site.webmanifest" />
          {env?.APP_ENV === "preview" && (
            <meta name="robots" content="noindex, nofollow" />
          )}
          <Meta />
          <Links />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(
                getOrganizationSchema(env?.BASE_URL || "https://schnerp.com"),
              ),
            }}
          />
          {serverStyleData?.map(({ key, ids, css }) => (
            <style
              key={key}
              data-emotion={`${key} ${ids.join(" ")}`}
              dangerouslySetInnerHTML={{ __html: css }}
            />
          ))}
          {/* SCHNERP ATTRIBUTION CAPTURE - runs on initial page load only, not SPA navigations */}
          <script src="/js/attribution.js" />
          {/* REWARDFUL INTEGRATION */}
          <script
            dangerouslySetInnerHTML={{
              __html: `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`,
            }}
          />
          <script
            defer
            src="https://r.wdfl.co/rw.js"
            data-rewardful={env?.REWARDFUL_PUBLIC_KEY}
          />
        </head>
        <body
          {...(colorMode && {
            className: `chakra-ui-${colorMode}`,
          })}
        >
          {children}
          {env?.APP_ENV !== "test" && <ScrollRestoration />}
          <script
            suppressHydrationWarning={true}
            dangerouslySetInnerHTML={{
              __html: `window.env=${JSON.stringify(env)};`,
            }}
          />
          <script
            suppressHydrationWarning={true}
            dangerouslySetInnerHTML={{
              __html: `window.user=${JSON.stringify(user)};`,
            }}
          />
          <Scripts />
        </body>
      </html>
    );
  },
);
